import { FC } from 'react';
import { HiOutlinePencilAlt, HiOutlineTrash, HiOutlineExternalLink, HiOutlineClock, HiOutlineCurrencyDollar, HiOutlineLocationMarker } from 'react-icons/hi';
import { DAV_MAIN_DOMAIN, TourProps } from '../../../../constants';
import { Card } from '../../../ui/Card';

interface TourCardProps {
  tour: TourProps;
  layout?: 'grid' | 'list';
  onEditHandler: () => void;
  onDeleteHandler: () => void;
}

const TourCard: FC<TourCardProps> = ({ tour, layout = 'grid', onEditHandler, onDeleteHandler }) => {
  const { name, slug, imageCover, price, duration, country, category } = tour;
  const isList = layout === 'list';
  
  return (
    <Card 
      variant='glass' 
      className={`
        group relative overflow-hidden border-none shadow-xl shadow-gray-200/50 dark:shadow-none 
        transition-all duration-500 hover:shadow-2xl hover:-translate-y-1
        ${isList ? 'h-full sm:h-56' : 'h-full'}
      `}
    >
      <div className={`flex flex-col ${isList ? 'sm:flex-row h-full' : 'h-full'}`}>
        {/* Image Section */}
        <div className={`relative flex-shrink-0 overflow-hidden ${isList ? 'w-full sm:w-72 h-48 sm:h-full' : 'w-full h-56 s'}`}>
          <img 
            src={imageCover} 
            alt={name} 
            className='w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110' 
          />
          
          {/* Gradients */}
          <div className='absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500' />
          
          {/* Floating Category Badge */}
          {category && (
            <div className='absolute top-3 left-3 z-10'>
              <span className='px-2.5 py-1 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 text-[10px] font-bold text-white uppercase tracking-wider'>
                {category}
              </span>
            </div>
          )}

          {/* Floating Price Tag (Desktop & Mobile) */}
          <div className='absolute bottom-3 left-3 z-10'>
            <div className='px-3 py-1.5 bg-primary-600 backdrop-blur-md rounded-xl text-xs font-black text-white flex items-center gap-1 shadow-lg shadow-primary-600/30 border border-primary-500/50'>
                <HiOutlineCurrencyDollar className='w-3.5 h-3.5' />
                {price?.toLocaleString()}
            </div>
          </div>

          {/* External Link Hover */}
          <div className='absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-4 group-hover:translate-y-0'>
             <div className='bg-white/20 backdrop-blur-md p-3 rounded-full border border-white/30'>
                <HiOutlineExternalLink className='w-5 h-5 text-white' />
             </div>
          </div>
        </div>

        {/* Content Section */}
        <div className='flex flex-col flex-1 p-5 min-w-0 bg-white/50 dark:bg-gray-800/10'>
          <div className='flex justify-between items-start gap-4'>
            <div className='flex-1'>
              <div className='flex items-center gap-2 mb-2'>
                  <div className='px-2 py-0.5 rounded-md bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20 flex items-center gap-1'>
                    <HiOutlineLocationMarker className='w-3 h-3' />
                    <span className='text-[10px] font-black tracking-widest uppercase'>
                      {country}
                    </span>
                  </div>
              </div>
              <h3 className='text-xl font-bold text-gray-900 dark:text-white line-clamp-2 leading-tight group-hover:text-primary-600 transition-colors'>
                {name}
              </h3>
              <a 
                href={`${DAV_MAIN_DOMAIN}/tours/${slug}`} 
                target='_blank'
                rel='noreferrer'
                className='inline-flex items-center gap-1.5 mt-2.5 text-[11px] font-bold text-gray-400 hover:text-primary-500 transition-all uppercase tracking-tight group/site'
              >
                View on live site
                <HiOutlineExternalLink className='w-3.5 h-3.5 transition-transform group-hover/site:translate-x-0.5 group-hover/site:-translate-y-0.5' />
              </a>
            </div>
          </div>

          <div className='mt-auto pt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-t border-gray-100 dark:border-gray-700/50'>
            {/* Meta Info */}
            <div className='flex items-center gap-4'>
                <div className='flex items-center gap-1.5 text-gray-500 dark:text-gray-400'>
                  <HiOutlineClock className='w-4 h-4 text-primary-500' />
                  <span className='text-xs font-bold tracking-tight'>{duration} Days</span>
                </div>
            </div>

            {/* Actions */}
            <div className='flex items-center gap-2'>
              <button
                type='button'
                onClick={(e) => { e.stopPropagation(); onEditHandler(); }}
                className='flex-1 sm:flex-none p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-primary-50 hover:text-primary-600 dark:hover:bg-primary-900/20 dark:hover:text-primary-400 transition-all flex items-center justify-center gap-2 group/btn border border-transparent hover:border-primary-200'
                title='Edit Package'
              >
                <HiOutlinePencilAlt className='w-5 h-5' />
                <span className='sm:hidden font-bold'>Edit</span>
              </button>
              
              <button
                type='button'
                onClick={(e) => { e.stopPropagation(); onDeleteHandler(); }}
                className='flex-1 sm:flex-none p-2.5 rounded-xl bg-rose-50 dark:bg-rose-900/10 text-rose-600 dark:text-rose-400 hover:bg-rose-100 hover:text-rose-700 dark:hover:bg-rose-900/30 transition-all flex items-center justify-center gap-2 border border-transparent hover:border-rose-200'
                title='Delete Package'
              >
                <HiOutlineTrash className='w-5 h-5' />
                <span className='sm:hidden font-bold'>Delete</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};


export default TourCard;
